import React, { useEffect, useState } from "react";
import {
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Checkbox,
    ListItemText,
    OutlinedInput,
    Typography,
    Alert,
} from "@mui/material";
import useSWR from "swr";
import ApexCharts from "react-apexcharts"; // Import ApexCharts for donut chart
import "./ProductsOverview.css";
import API_ENDPOINTS from "../../../apis/endpoints";
import { PieChart } from "../../../components/Charts";
import { Card } from "../../../components/Card";
import LastRefreshed from "../LastRefreshed";

// Fetcher function for SWR

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const ProductsOverview = () => {
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [sortColumn, setSortColumn] = useState("feature");
    const [sortDirection, setSortDirection] = useState("asc");

    // Fetch product list for multi-select dropdown
    const { data: productListDataset, error: productListError } = useSWR(
        `${API_ENDPOINTS.PRODUCT_LIST}`
    );

    const { data: productListData, refreshed_at } = productListDataset ?? {};

    // Fetch roadmap data based on selected products
    const {
        data: roadmapData,
        error: roadmapError,
        isLoading,
    } = useSWR(
        `${API_ENDPOINTS.ROADMAP}?products=${selectedProducts.join(",")}`
    );

    const products = roadmapData ?? [];

    // Handle sorting
    const handleSortRequest = (column) => {
        const isAsc = sortColumn === column && sortDirection === "asc";
        setSortDirection(isAsc ? "desc" : "asc");
        setSortColumn(column);

        // Sort data based on column and direction
        products.sort((a, b) => {
            if (isAsc) return a[column] > b[column] ? 1 : -1;
            return a[column] < b[column] ? 1 : -1;
        });
    };

    // Handle page change for pagination
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    // Handle rows per page change
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0); // Reset to first page when changing rows per page
    };

    // Handle product selection change in multi-select dropdown
    const handleProductChange = (event) => {
        const {
            target: { value },
        } = event;
        setSelectedProducts(
            typeof value === "string" ? value.split(",") : value
        );
    };

    // Set default selection to all products when product list is fetched
    useEffect(() => {
        if (
            productListData &&
            productListData?.length > 0 &&
            selectedProducts?.length === 0
        ) {
            const allProductsKeys = productListData.map(
                (product) => product.key
            );
            setSelectedProducts(allProductsKeys); // Select all by default
        }
    }, [productListData]);

    // Calculate chart data for Overall Progress

    return (
        <Card>
            {/* Header Section */}
            <div className="header-section">
                <Typography variant="highlightSemibold">
                    Products Overview
                </Typography>
                <FormControl sx={{ m: 1, width: 300 }}>
                    <InputLabel id="product-multi-select-label">
                        All Products
                    </InputLabel>
                    <Select
                        labelId="product-multi-select-label"
                        id="product-multi-select"
                        multiple
                        value={selectedProducts}
                        onChange={handleProductChange}
                        input={<OutlinedInput label="All Products" />}
                        renderValue={(selected) =>
                            productListData
                                ?.filter((product) =>
                                    selected.includes(product.key)
                                )
                                .map((product) => product.name)
                                .join(", ")
                        }
                        MenuProps={MenuProps}
                    >
                        {productListData?.map((product) => (
                            <MenuItem key={product.key} value={product.key}>
                                <Checkbox
                                    checked={
                                        selectedProducts.indexOf(product.key) >
                                        -1
                                    }
                                />
                                <ListItemText primary={product.name} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>

            {/* Top Section */}
            <div className="top-section">
                {/* Overall Progress Chart */}

                {isLoading ? (
                    <CircularProgress />
                ) : (
                    <PieChart
                        data={products}
                        xKey="status"
                        series={[
                            {
                                key: "Backlog",
                                name: "Backlog",
                            },
                            {
                                key: "In Progress",
                                name: "In Progress",
                            },
                            {
                                key: "Delievered",
                                name: "Delievered",
                            },
                        ]}
                        title="Overall Progress"
                    />
                )}

                {/* Overall Highlights */}
                {/* <div className="overall-highlights"> */}
                <Card>
                    <Typography variant="contentAccent" sx={{ margin: "1rem" }}>
                        Overall Highlights
                    </Typography>
                    {isLoading ? (
                        <CircularProgress />
                    ) : (
                        <>
                            {products.length > 0 ? (
                                products.map((product, index) =>
                                    product.highlights ? (
                                        <div
                                            key={index}
                                            className="highlights-content"
                                            dangerouslySetInnerHTML={{
                                                __html: product?.highlights.replace(
                                                    /\n/g,
                                                    "<br />"
                                                ),
                                            }}
                                        />
                                    ) : null
                                )
                            ) : (
                                <p>No highlights available</p>
                            )}
                        </>
                    )}
                </Card>
            </div>

            {/* Bottom Section */}
            {/* <div className="bottom-section"> */}
            <Card>
                <div className="roadmap-header">
                    <h3>Product Roadmap</h3>
                    {/* <Button variant="contained">Filter</Button> */}
                </div>

                {/* Product Roadmap Table */}
                {isLoading ? (
                    <CircularProgress />
                ) : roadmapError ? (
                    <p>Error loading products: {roadmapError.message}</p>
                ) : (
                    <Paper className="table-paper">
                        {/* Fixed height table with sticky header */}
                        <TableContainer className="table-container">
                            <Table
                                stickyHeader
                                aria-label="product roadmap table"
                            >
                                <TableHead>
                                    <TableRow>
                                        {" "}
                                        {/* primary-50 color */}
                                        <TableCell>Sr. No.</TableCell>
                                        <TableCell
                                            onClick={() =>
                                                handleSortRequest("product")
                                            }
                                        >
                                            Product Name ↑↓{" "}
                                            {/* Add sort indicator */}
                                        </TableCell>
                                        <TableCell
                                            onClick={() =>
                                                handleSortRequest("feature")
                                            }
                                        >
                                            Feature Name ↑↓{" "}
                                            {/* Add sort indicator */}
                                        </TableCell>
                                        <TableCell>Description</TableCell>
                                        <TableCell>Status</TableCell>
                                    </TableRow>
                                </TableHead>

                                {/* Table Body */}
                                <TableBody>
                                    {products
                                        .slice(
                                            page * rowsPerPage,
                                            page * rowsPerPage + rowsPerPage
                                        )
                                        .map((product, index) => (
                                            <TableRow key={index}>
                                                <TableCell>
                                                    {index + 1}
                                                </TableCell>{" "}
                                                {/* Row number */}
                                                <TableCell>
                                                    {product.product}
                                                </TableCell>
                                                <TableCell>
                                                    {product.feature}
                                                </TableCell>
                                                <TableCell>
                                                    {product.description}
                                                </TableCell>
                                                <TableCell>
                                                    {product.status}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Pagination */}
                        <TablePagination
                            component="div"
                            count={products.length}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            labelRowsPerPage="Rows per page:"
                        />
                    </Paper>
                )}
            </Card>

            <LastRefreshed date={refreshed_at} />
        </Card>
    );
};

export default ProductsOverview;
